import Box from '@mui/material/Box';
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
} from '@mui/material';
import { useLangFile } from '../../../../../context/LanguageContext';
import moment from 'moment';
import SeedingCalendarDay, { SeedingCalendarRangeContext } from './SeedingCalendarDay';
import { allSoilTypes, SOIL_TYPE } from '../../../../../constants/SoilType';
import { allClimates, CLIMATE } from '../../../../../constants/Climate';
import { allSeedbedQualities, SEEDBED_QUALITY } from '../../../../../constants/SeedbedQuality';
import { clsx } from 'clsx';
import SeedingCalcNonHybridInputs from './SeedingCalcNonHybridInputs';
import { Crop, CropType } from '../../../../../constants/CropConstants';
import { getDefaultUnitsPerSack, isCropHybrid } from './SeedingCalcUtils';
import SeedingCalcHybridInputs from './SeedingCalcHybridInputs';
import { makeStyles } from '@mui/styles';
import { setSeedingCalculatorParams } from '../../../../../reducers/PrescriptionReducer';
import { useSavedInput } from './useSavedInput';
import { useSeedingPrescriptionLimitCalculator } from './useSeedingPrescriptionLimitCalculator';
import { cropData, getSeedRate } from '../../../DanishSeedingModelV1SeedRate';
import {
  Button,
  ButtonBase,
  Checkbox,
  IconButton,
  TrackedDesktopDatePicker,
  TrackedSelectFormControl,
} from '../../../../Analytics/TrackedComponents';
import { ANALYTICS_EVENTS } from '../../../../Analytics/AnalyticsConstants';
import { SeedingCalculatorStrategy } from './SeedingCalculatorStrategy/SeedingCalculatorStrategy';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gridColumnGap: '2px',
    alignItems: 'start',
    width: '100%',
  },
  organicCheckboxWithMargin: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '15px',
  },
  organicCheckbox: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '0px',
  },
  iconButton: {
    gridColumn: 1,
    gridRow: 1,
    alignSelf: 'center',
    padding: 6,
    minWidth: 'auto',
  },
  headerButton: {
    gridColumn: 2,
    gridRow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    padding: 0,
    borderRadius: 0,
    textAlign: 'left',
    height: '100%',
  },
  headerTypography: {
    width: '100%',
    textAlign: 'left',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  content: {
    gridColumn: 2,
    gridRow: 2,
    marginTop: '1px',
  },
  useCalcResultButton: {
    background: 'transparent',
  },
}));

interface SeedingCalcProps {
  handleOnClickUseCalcResult: (calcResult: number) => void;
  isBulkPrescription: boolean;
  adjustPrescriptionBasedOnSoilAnalysis: boolean;
  dispatch: React.Dispatch<React.SetStateAction<string>>;
  strategy: SeedingCalculatorStrategy;
}

const cropsAvailableForSeedingCalculator = [
  CropType.WHEAT_WINTER,
  CropType.BARLEY_WINTER_TWO_ROW,
  CropType.BARLEY_WINTER_SIX_ROW,
  CropType.WINTER_BARLEY_HYBRID,
  CropType.RYE_WINTER,
  CropType.RYE_HYBRID_WINTER,
  CropType.TRITICALE_WINTER,
  CropType.RAPESEED_WINTER,
  CropType.NONE,
];

const SeedingCalc: React.FC<SeedingCalcProps> = forwardRef((props: SeedingCalcProps, ref) => {
  const [isSeedingCalculatorExpanded, setIsSeedingCalculatorExpanded] = useState(true);
  const LangFile = useLangFile();

  const [organic, setOrganic] = useSavedInput('isOrganic', false);
  const [TGW, setTGW] = useSavedInput('tgw', 0);
  const [germinationPercentage, setGerminationPercentage] = useSavedInput(
    'germinationPercentage',
    90
  );
  const [seedingDate, setSeedingDate] = useSavedInput('seedingDate', null);
  const [seedbedQuality, setSeedbedQuality] = useSavedInput(
    'seedbedQuality',
    SEEDBED_QUALITY.NORMAL
  );
  const [soilType, setSoilType] = useSavedInput('soilType', SOIL_TYPE.NORMAL);
  const [climate, setClimate] = useSavedInput('climate', CLIMATE.NORMAL);
  const [selectedCrop, setSelectedCrop] = useSavedInput('selectedCrop', CropType.NONE);
  const [unitsPerSack, setUnitsPerSack] = useSavedInput(
    'unitsPerSack',
    getDefaultUnitsPerSack(selectedCrop) || 0
  );
  const [sackWeight, setSackWeight] = useSavedInput('sackWeight', 0);
  const [useCalculationPressed, setUseCalculationPressed] = useState(false);

  const setCropWithDefaultUnits = useCallback(
    (crop: Crop) => {
      setSelectedCrop(crop);
      const defaultUnits = getDefaultUnitsPerSack(crop);
      if (defaultUnits !== null) {
        setUnitsPerSack(defaultUnits);
      }
    },
    [setSelectedCrop, setUnitsPerSack]
  );

  const calculatorParams = useMemo(
    () => ({
      selectedCrop,
      organic,
      TGW,
      germinationPercentage,
      unitsPerSack,
      sackWeight,
      seedbedQuality,
      seedingDate,
      soilType,
      climate,
    }),
    [
      selectedCrop,
      organic,
      TGW,
      germinationPercentage,
      unitsPerSack,
      sackWeight,
      seedbedQuality,
      seedingDate,
      soilType,
      climate,
    ]
  );
  const {
    calculationResult,
    shouldShowOutsideRangeWarning,
    requestSeedingDateChange,
    requestCropChange,
    confirmOutsideRangeDate,
    cancelOutsideRangeDate,
  } = useSeedingPrescriptionLimitCalculator(
    calculatorParams,
    setSeedingDate,
    setCropWithDefaultUnits,
    props.strategy
  );
  const seedingCalcResult = useMemo(
    () => (calculationResult === null ? null : calculationResult.toFixed(0)),
    [calculationResult]
  );

  const handleToggleSeedingCalculator = useCallback(() => {
    setIsSeedingCalculatorExpanded(!isSeedingCalculatorExpanded);
  }, [isSeedingCalculatorExpanded, setIsSeedingCalculatorExpanded]);

  const handleCheckOrganic = useCallback(
    (event) => {
      setOrganic(event.target.checked);
    },
    [setOrganic]
  );

  const isSelectedCropHybrid = useMemo(() => {
    return isCropHybrid(selectedCrop);
  }, [selectedCrop]);

  const seedbedQualityOptions = useMemo(() => {
    return allSeedbedQualities().map((seedbedQuality) => {
      return {
        value: seedbedQuality,
        label: LangFile.SeedbedQuality[seedbedQuality],
      };
    });
  }, [LangFile.SeedbedQuality]);

  const soilTypeOptions = useMemo(() => {
    return allSoilTypes().map((soilType) => {
      return {
        value: soilType,
        label: LangFile.SoilType[soilType],
      };
    });
  }, [LangFile.SoilType]);

  const climateOptions = useMemo(() => {
    return allClimates().map((climate) => ({
      value: climate,
      label: LangFile.Climate[climate],
    }));
  }, [LangFile.Climate]);

  const cropOptions = useMemo(() => {
    return cropsAvailableForSeedingCalculator.map((crop) => {
      return {
        value: crop,
        label: LangFile.CropType[crop],
      };
    });
  }, [LangFile.CropType]);

  const isDateInSelectedCropRange = useCallback(
    (date: Date) => {
      if (Number.isNaN(date.getTime()) || !cropData[selectedCrop]) return false;
      return !getSeedRate(selectedCrop, date).isOutsideRange;
    },
    [selectedCrop]
  );

  const handleOnClickUseCalcResult = useCallback(() => {
    setUseCalculationPressed(true);
    if (seedingCalcResult) {
      props.handleOnClickUseCalcResult(seedingCalcResult);
    }
  }, [seedingCalcResult, props.handleOnClickUseCalcResult]);

  const commonStylesForSeeingCalc = {
    size: 'small',
    disablePadding: true,
    variant: 'outlined',
  };
  const classes = useStyles();

  useEffect(() => {
    if (props.adjustPrescriptionBasedOnSoilAnalysis && soilType !== SOIL_TYPE.NORMAL) {
      setSoilType(SOIL_TYPE.NORMAL);
    }
  }, [props.adjustPrescriptionBasedOnSoilAnalysis, soilType, setSoilType]);

  useImperativeHandle(ref, () => {
    return {
      saveSeedingCalcParamsToStore: () => {
        if (useCalculationPressed && seedingCalcResult !== null) {
          props.dispatch(
            setSeedingCalculatorParams({
              isOrganic: organic,
              tgw: TGW,
              germinationPercentage: germinationPercentage,
              unitsPerSack: unitsPerSack,
              sackWeight: sackWeight,
              seedingDate: seedingDate,
              seedbedQuality: seedbedQuality,
              soilType: soilType,
              climate: climate,
              selectedCrop: selectedCrop,
              result: parseFloat(String(seedingCalcResult)),
              identifier: props.strategy.identifier,
              version: props.strategy.version,
            })
          );
        }
      },
    };
  });

  return (
    <>
      <Box
        className={classes.root}
        sx={{
          bgcolor: 'grey.200',
          padding: '8px',
          marginBottom: '12px',
        }}
      >
        <IconButton
          onClick={handleToggleSeedingCalculator}
          className={classes.iconButton}
          {...ANALYTICS_EVENTS.toggle('seeding_calculator_section', {
            expanded: !isSeedingCalculatorExpanded,
          })}
        >
          {isSeedingCalculatorExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>

        <ButtonBase
          onClick={handleToggleSeedingCalculator}
          className={classes.headerButton}
          {...ANALYTICS_EVENTS.toggle('seeding_calculator_section', {
            expanded: !isSeedingCalculatorExpanded,
          })}
        >
          <Typography variant={'subtitle1'} className={classes.headerTypography}>
            {LangFile.PrescriptionSettings.seedingCalc.header}
          </Typography>
        </ButtonBase>

        {isSeedingCalculatorExpanded && (
          <Grid container spacing={1} className={classes.content}>
            <Grid size={4}>
              <TrackedSelectFormControl
                data-testid="seeding-crop"
                {...ANALYTICS_EVENTS.selector('seeding_calculator_crop')}
                label={LangFile.PrescriptionSettings.seedingCalc.crop.title}
                value={selectedCrop ? selectedCrop : ''}
                onChange={requestCropChange}
                options={cropOptions}
                helperText={
                  props.isBulkPrescription
                    ? LangFile.PrescriptionSettings.seedingCalc.crop.bulkPrescriptionDisclaimer
                    : ''
                }
                {...commonStylesForSeeingCalc}
              />
            </Grid>

            <Grid
              size={3}
              className={clsx(classes.organicCheckboxWithMargin, {
                [classes.organicCheckbox]: props.isBulkPrescription,
              })}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    slotProps={{ input: { 'data-testid': 'seeding-organic' } }}
                    {...ANALYTICS_EVENTS.checkbox('seeding_calculator_organic')}
                    onChange={handleCheckOrganic}
                    checked={organic}
                    disableRipple
                    color={'primary'}
                  />
                }
                label={LangFile.PrescriptionSettings.seedingCalc.organic}
              />
            </Grid>

            {isSelectedCropHybrid ? (
              <SeedingCalcHybridInputs
                sackWeight={sackWeight}
                unitsPerSack={unitsPerSack}
                handleUpdateUnitsPerSack={setUnitsPerSack}
                handleUpdateSackWeight={setSackWeight}
              />
            ) : (
              <SeedingCalcNonHybridInputs
                TGW={TGW}
                handleOnUpdateGerminationPercentage={setGerminationPercentage}
                germinationPercentage={germinationPercentage}
                handleOnUpdateTGW={setTGW}
              />
            )}

            <Grid size={2}>
              <Typography>{LangFile.PrescriptionSettings.seedingCalc.seedingTime.title}</Typography>

              <SeedingCalendarRangeContext.Provider value={isDateInSelectedCropRange}>
                <TrackedDesktopDatePicker
                  {...ANALYTICS_EVENTS.selector('seeding_calculator_seeding_date')}
                  analyticsExtraParams={(date) => ({
                    is_within_seeding_range: date
                      ? isDateInSelectedCropRange(date.toDate())
                      : false,
                  })}
                  format="DD/MM/YYYY"
                  value={seedingDate ? moment(seedingDate) : null}
                  onChange={(date) => {
                    if (!date) {
                      requestSeedingDateChange(null);
                      return;
                    }
                    const selectedDate = date.toDate();
                    if (Number.isNaN(selectedDate.getTime())) return;
                    requestSeedingDateChange(selectedDate);
                  }}
                  disablePast={true}
                  slots={{ day: SeedingCalendarDay }}
                  slotProps={{
                    textField: {
                      slotProps: { htmlInput: { 'data-testid': 'seeding-date' } },
                      style: { marginTop: '7px' },
                      size: 'small',
                    },
                  }}
                />
              </SeedingCalendarRangeContext.Provider>
            </Grid>

            <Grid size={2}>
              <TrackedSelectFormControl
                data-testid="seeding-seedbed-quality"
                {...ANALYTICS_EVENTS.selector('seeding_calculator_seedbed_quality')}
                label={LangFile.PrescriptionSettings.seedingCalc.seedbedQuality}
                value={seedbedQuality ? seedbedQuality : ''}
                onChange={setSeedbedQuality}
                options={seedbedQualityOptions}
                {...commonStylesForSeeingCalc}
              />
            </Grid>

            <Grid size={2}>
              <TrackedSelectFormControl
                data-testid="seeding-soil-type"
                {...ANALYTICS_EVENTS.selector('seeding_calculator_soil_type')}
                label={LangFile.PrescriptionSettings.seedingCalc.soilType}
                value={soilType ? soilType : ''}
                onChange={setSoilType}
                options={soilTypeOptions}
                disabled={props.adjustPrescriptionBasedOnSoilAnalysis}
                {...commonStylesForSeeingCalc}
              />
            </Grid>

            <Grid size={2}>
              <TrackedSelectFormControl
                data-testid="seeding-climate"
                {...ANALYTICS_EVENTS.selector('seeding_calculator_climate')}
                label={LangFile.PrescriptionSettings.seedingCalc.climate}
                value={climate ? climate : ''}
                onChange={setClimate}
                options={climateOptions}
                {...commonStylesForSeeingCalc}
              />
            </Grid>

            <Grid size={4} style={{ alignSelf: 'end' }}>
              <Button
                data-testid="seeding-use-calculation-result"
                {...ANALYTICS_EVENTS.button('seeding_calculator_use_result')}
                disabled={!seedingCalcResult}
                fullWidth={true}
                variant="outlined"
                style={{ marginBottom: '4px', height: '40px' }}
                className={classes.useCalcResultButton}
                onClick={handleOnClickUseCalcResult}
              >
                <Typography>
                  {LangFile.PrescriptionSettings.seedingCalc.useResult +
                    (seedingCalcResult ? `:  ${seedingCalcResult?.toString()} kg/ha` : '')}
                </Typography>
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>

      <Dialog
        open={shouldShowOutsideRangeWarning}
        onClose={cancelOutsideRangeDate}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {LangFile.PrescriptionSettings.seedingCalc.outsideRangeWarning.title}
        </DialogTitle>
        <DialogContent>
          {LangFile.PrescriptionSettings.seedingCalc.outsideRangeWarning.message}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={cancelOutsideRangeDate}
            color="inherit"
            variant="outlined"
            {...ANALYTICS_EVENTS.button('outside-range-warning', { action: 'cancelled' })}
          >
            {LangFile.PrescriptionSettings.seedingCalc.outsideRangeWarning.cancel}
          </Button>
          <Button
            onClick={confirmOutsideRangeDate}
            color="primary"
            variant="contained"
            {...ANALYTICS_EVENTS.button('outside-range-warning', { action: 'confirmed' })}
          >
            {LangFile.PrescriptionSettings.seedingCalc.outsideRangeWarning.confirm}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default memo(SeedingCalc);
